import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import type {
  IActivityCategory,
  IActivityOccurrenceOrGroupWithRequiredPatient,
  ITimeOfDay,
} from "@medoma/models/activities";
import {
  getActivityCategoryConfig,
  isGroup,
  timeOfDaySchema,
  groupStatusSchema,
} from "@medoma/models/activities";
import { useMemo } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import styles from "./ActivityTable.module.scss";
import { useNavigate, useParams } from "react-router";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import clsx from "clsx";
import { ActorsCell } from "@/pages/commandcenter/Activities/ActivitiesTable/ActorsCell";
import { Text } from "@medoma/components/Text/Text";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { TimeSpan } from "@/components/Time/TimeSpan";
import { useLingui } from "@lingui/react/macro";
import { calculateGroupStatus } from "@/pages/commandcenter/Activities/ActivitiesTable/getDataFromActivityOccurrences";
import type { IStatus } from "@/components/StatusTag/statusTagUtils";
import {
  activityCategoryDictionary,
  displayCancelGroupReason,
  isCancelledWithReason,
} from "@/api/Activities";
import DurationChip from "@/components/Chips/DurationChip";
import { calculateGroupDuration } from "@/components/Chips/calculateGroupDuration";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";

interface IActivityTable {
  activityOccurrencesAndGroups: IActivityOccurrenceOrGroupWithRequiredPatient[];
}

type IActivityTableItem = {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroupWithRequiredPatient;
  category: IActivityCategory;
  title: IActivityOccurrenceOrGroupWithRequiredPatient;
  timespan: [Date, Date, ITimeOfDay];
  status: IStatus;
  actors: IActivityOccurrenceOrGroupWithRequiredPatient;
  occurrenceIsActive: boolean;
  activityIsActive: boolean;
  groupIsActive: boolean;
  activeOccurrencesInGroup: { activityId: string; occurrenceId: string }[];
  activeActivitiesInGroup: { activityId: string }[];
  estimatedDuration: number;
};

const columnHelper = createColumnHelper<IActivityTableItem>();

const columns = ({
  translator,
  showDuration = false,
}: {
  translator: ReturnType<typeof useLingui>["t"];
  showDuration?: boolean;
}) => [
  columnHelper.accessor("category", {
    header: translator(msg`Typ av besök`),
    cell: ({ getValue }) => {
      return <CategoryIcon category={getValue()} size="small" />;
    },
  }),
  columnHelper.accessor("timespan", {
    header: translator(msg`Tid`),
    cell: ({ getValue, row: { original } }) => {
      const [start, end, type] = getValue();
      const categoryAllowsDuration = getActivityCategoryConfig({
        category: original.category,
        activityHasPatient: true,
      }).allowsDuration;
      return (
        <div className={styles.timespanCell}>
          <TimeSpan
            timespan={type === timeOfDaySchema.enum.Any ? type : { start, end }}
          />
          {showDuration && categoryAllowsDuration ? (
            <DurationChip minutes={original.estimatedDuration} />
          ) : null}
        </div>
      );
    },
  }),
  columnHelper.accessor("status", {
    header: translator(msg`Status`),
    cell: ({ getValue }) => {
      const status = getValue();

      if (!status) {
        // Fallback to no status tag
        return null;
      }

      return <StatusTag status={status} variant="icon" size="small" />;
    },
  }),
  columnHelper.accessor("title", {
    header: translator(msg`Aktivitet`),
    cell: ({ getValue, row }) => {
      const activityOccurrenceOrGroup = getValue();
      if (!isGroup(activityOccurrenceOrGroup)) {
        return (
          <ActivityTitle
            activityOccurrence={activityOccurrenceOrGroup}
            weight="medium"
            size="small"
            showDescriptionAndComment
            showRecurringIcon
          />
        );
      }

      const isCancelledVisit =
        activityOccurrenceOrGroup.visitStatus ===
        groupStatusSchema.enum.cancelled;
      const cancellationReason = isCancelledWithReason(
        activityOccurrenceOrGroup,
      )
        ? displayCancelGroupReason(
            activityOccurrenceOrGroup.visitCancellationReason,
            translator,
          )
        : undefined;

      return (
        <>
          <Text element="span" weight="medium" size="small">
            {translator(
              activityCategoryDictionary[activityOccurrenceOrGroup.category],
            )}
            {isCancelledVisit ? (
              <Text
                element="span"
                weight="regular"
                size="small"
                className={styles.cancellationInfo}
              >
                <span className={styles.cancellationSeparator} aria-hidden />
                <Trans>Avbokat</Trans>
                {cancellationReason ? (
                  <>
                    <span
                      className={styles.cancellationSeparator}
                      aria-hidden
                    />
                    {cancellationReason}
                  </>
                ) : null}
              </Text>
            ) : null}
          </Text>
          <ul>
            {activityOccurrenceOrGroup.occurrences.map((activityOccurrence) => {
              const { id, activityId } = activityOccurrence;

              return (
                <li
                  key={`${activityId}-${id}`}
                  className={clsx(
                    styles.activityTitleInGroup,
                    row.original.activeOccurrencesInGroup.find(
                      (item) =>
                        activityId === item.activityId &&
                        id === item.occurrenceId,
                    ) && styles.occurrenceIsActive,
                    row.original.activeActivitiesInGroup.find(
                      (item) => activityId === item.activityId,
                    ) && styles.activityIsActive,
                  )}
                >
                  <ActivityTitle
                    activityOccurrence={activityOccurrence}
                    weight="regular"
                    size="small"
                    statusTag={
                      <StatusTag
                        status={activityOccurrence.status}
                        size="tiny"
                      />
                    }
                    showDescriptionAndComment
                    showRecurringIcon
                  />
                </li>
              );
            })}
          </ul>
        </>
      );
    },
  }),
  columnHelper.accessor("actors", {
    header: translator(msg`Utförare`),
    cell: ({ getValue }) => {
      const activityOccurrenceOrGroup = getValue();
      return (
        <ActorsCell activityOccurrenceOrGroup={activityOccurrenceOrGroup} />
      );
    },
  }),
];

const ActivityTable = ({ activityOccurrencesAndGroups }: IActivityTable) => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useLingui();
  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const data = useMemo(
    () =>
      activityOccurrencesAndGroups.map((activityOccurrenceOrGroup) => {
        if (isGroup(activityOccurrenceOrGroup)) {
          const group = activityOccurrenceOrGroup;
          return {
            activityOccurrenceOrGroup: group,
            category: group.category,
            timespan: [
              group.start,
              group.end,
              group.occurrences.every(
                ({ timeOfDay }) => timeOfDay === timeOfDaySchema.enum.Any,
              )
                ? timeOfDaySchema.enum.Any
                : timeOfDaySchema.enum.Specific,
            ] as [Date, Date, ITimeOfDay],
            status: calculateGroupStatus(group),
            title: group,
            actors: group,
            groupIsActive: params.groupId === group.id,
            occurrenceIsActive: false,
            activityIsActive: false,
            activeOccurrencesInGroup: group.occurrences
              .filter(
                (occurrence) =>
                  params.activityId === occurrence.activityId &&
                  params.occurrenceId === occurrence.id,
              )
              .map((occurrence) => ({
                activityId: occurrence.activityId,
                occurrenceId: occurrence.id,
              })),
            activeActivitiesInGroup: group.occurrences
              .filter(
                (occurrence) => params.activityId === occurrence.activityId,
              )
              .map((occurrence) => ({
                activityId: occurrence.activityId,
              })),
            estimatedDuration: calculateGroupDuration(
              group.occurrences.map((o) => o.duration),
            ),
          };
        } else {
          const activityOccurrence = activityOccurrenceOrGroup;
          return {
            activityOccurrenceOrGroup: activityOccurrence,
            category: activityOccurrence.category,
            timespan: [
              activityOccurrence.start,
              activityOccurrence.end,
              activityOccurrence.timeOfDay,
            ] as [Date, Date, ITimeOfDay],
            status: activityOccurrence.status,
            title: activityOccurrence,
            patient: activityOccurrence.patient,
            actors: activityOccurrence,
            occurrenceIsActive:
              params.occurrenceId === activityOccurrenceOrGroup.id &&
              params.activityId === activityOccurrenceOrGroup.activityId,
            activityIsActive:
              params.activityId === activityOccurrenceOrGroup.activityId,
            groupIsActive: false,
            activeOccurrencesInGroup: [],
            activeActivitiesInGroup: [],
            estimatedDuration: activityOccurrence.duration,
          };
        }
      }),
    [
      activityOccurrencesAndGroups,
      params.activityId,
      params.occurrenceId,
      params.groupId,
    ],
  );

  const translatedColumns = columns({
    translator: t,
    showDuration: showDuration ?? false,
  });

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const table = useReactTable({
    columns: translatedColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table className={styles.table}>
      <thead className={styles.thead}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className={styles.tbody}>
        {table.getRowModel().rows.map((row) => {
          const item = row.original.activityOccurrenceOrGroup;
          const isCancelledVisit =
            isGroup(item) &&
            item.visitStatus === groupStatusSchema.enum.cancelled;

          // Temporary fix for a11y - we aim to move to React Aria Table soon
          const rowAriaLabel = isGroup(item)
            ? `${t(activityCategoryDictionary[item.category])}: ${item.occurrences.map((occurrence) => occurrence.title).join(", ")}`
            : item.title;

          const navigateToRow = () => {
            if (isGroup(item)) {
              void navigate(`occurrence-groups/${item.id}`);
            } else {
              void navigate(`${item.activityId}/occurrences/${item.id}`);
            }
          };

          return (
            <tr
              key={row.id}
              // Temporary fix for a11y - we aim to move to React Aria Table soon
              tabIndex={0}
              aria-label={rowAriaLabel}
              className={clsx(
                row.original.occurrenceIsActive && styles.occurrenceIsActive,
                row.original.activityIsActive && styles.activityIsActive,
                row.original.groupIsActive && styles.groupIsActive,
                isCancelledVisit && styles.cancelled,
              )}
              onClick={(event) => {
                // Don't navigate if the target is a button, link, or button-like element
                // E.g. HiddenForPatientPopover
                const el = event.target;
                if (
                  el instanceof Element &&
                  el.closest('button, a, [role="button"]')
                ) {
                  return;
                }
                navigateToRow();
              }}
              // Temporary fix for a11y - we aim to move to React Aria Table soon
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) {
                  return;
                }
                if (event.key !== "Enter") {
                  return;
                }
                navigateToRow();
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ActivityTable;
